import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { Zap, X, Loader2, Check, Edit } from "lucide-react";
import { useSuperAdmin } from "../../context/SuperAdminContext"

const COLOR = "#3B82F6";
export default function ManageSkills() {


  const { fetchAllSkills, addSkill, adding, togglingId, skillStatusChange, editSkill, savingEdit } = useSuperAdmin();

  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [skillName, setSkillName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");

  const getToken = () => localStorage.getItem("superadmintoken");
  const getHeaders = () => ({
    Authorization: `Bearer ${getToken()}`,
    "Content-Type": "application/json",
  });

  // ── Fetch Skills ───────────────────────────────────────────────────────────
  const fetchSkills = useCallback(async () => {
  setLoading(true);
  try {
    const res = await fetchAllSkills(false); // Changed from fetchallskills
    console.log("Skills API Response:", res); // Debug log
    
    // Check the response structure
    if (res?.data?.success && Array.isArray(res.data.skills)) {
      setSkills(
        res.data.skills.map((item) => ({
          id: item._id || item.id,
          name: item.name || item.skillName || "",
          isActive: item.isActive ?? true,
        }))
      );
    } else {
      console.error("Unexpected response structure:", res);
      toast.error("Invalid response format from server");
    }
  } catch (err) {
    console.error("Fetch skills error:", err);
    toast.error(err.response?.data?.message || "Failed to load skills");
  } finally {
    setLoading(false);
  }
}, [fetchAllSkills]);

  useEffect(() => { fetchSkills(); }, [fetchSkills]);

  // ── Add Skill ──────────────────────────────────────────────────────────────
  const handleAdd = async () => {
    try {
      const res = await addSkill(skillName);
      if (res.data.success) {
        toast.success("Skill added successfully");
        setSkillName("");
        setShowForm(false);
        await fetchSkills();
      } else {
        toast.error(res.data.message || "Failed to add");
      }
    } catch (err) {
      console.error("Add skill error:", err);
      toast.error(err.response?.data?.message || "Failed to add");
    }
  };

  // ── Toggle Active / Inactive ───────────────────────────────────────────────
  const handleToggle = async (skill) => {
    try {
      const res = await skillStatusChange(skill);
      if (res.data.success) {
        toast.success(`Skill marked as ${!skill.isActive ? "Active" : "Inactive"}`);
        setSkills((prev) =>
          prev.map((s) => s.id === skill.id ? { ...s, isActive: !s.isActive } : s)
        );
      } else {
        toast.error(res.data.message || "Failed to update status");
      }
    } catch (err) {
      console.error("Toggle error:", err);
      toast.error(err.response?.data?.message || "Failed to update status");
    }
  };

  // ── Edit Skill Name ────────────────────────────────────────────────────────
  const startEdit = (skill) => {
    setEditingId(skill.id);
    setEditValue(skill.name);
  };
  const cancelEdit = () => { setEditingId(null); setEditValue(""); };

  const handleSaveEdit = async (skill) => {
    try {
      const res = await editSkill(editValue, skill);
      if (res.data.success) {
        toast.success("Skill updated");
        setSkills((prev) =>
          prev.map((s) => s.id === skill.id ? { ...s, name: editValue.trim() } : s)
        );
        cancelEdit();
      } else {
        toast.error(res.data.message || "Failed to update");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update");
    } 
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="p-6" style={{ fontFamily: "'Segoe UI', sans-serif" }}>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Manage Skills</h2>
          <p className="text-s text-gray-400">
            {loading ? "Loading..." : `Total Skills : ${skills.length}`}
          </p>
        </div>
        <button
          onClick={() => { setShowForm((v) => !v); setSkillName(""); }}
          className="px-4 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 flex items-center justify-center gap-1"
        >
          <span>Add Skill</span>
        </button>
      </div>

      {/* Add Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-2">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add New Skill</h3>
              <X
                className="h-5 w-5 cursor-pointer text-gray-500 hover:text-gray-700"
                onClick={() => setShowForm(false)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Skill</label>
            </div>
            <div className="flex gap-2">
              <input
                value={skillName}
                onChange={(e) => setSkillName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                placeholder="Enter skill name"
                disabled={adding}
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md"
              />
            </div>
            <div className="my-2">
              <button
                onClick={handleAdd}
                disabled={adding}
                className="px-3 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 disabled:bg-indigo-300"
              >
                {adding ? <><Loader2 size={14} className="animate-spin inline mr-1" />Saving...</> : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      {!loading && skills.length > 0 && (
        <div className="w-full">
          <div className="max-h-[70vh] overflow-y-auto overflow-x-auto relative">
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 bg-white shadow-md z-[50]">
                <tr className="bg-gray-100">
                  <th className="p-2 text-left">Skill Name</th>
                  <th className="p-2 text-center">Status</th>
                  <th className="p-2 text-center">Edit</th>
                </tr>
              </thead>
              <tbody>
                {skills.map((skill) => (
                  <tr key={skill.id} className="border-b hover:bg-gray-50">

                    {/* Skill Name */}
                    <td className="p-2 text-left min-w-[400px]">
                      <div className="flex items-center gap-2">
                        {editingId === skill.id ? (
                          <div className="flex items-center gap-2 flex-1">
                            <input
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") handleSaveEdit(skill);
                                if (e.key === "Escape") cancelEdit();
                              }}
                              autoFocus
                              className="flex-1 px-2 py-1 text-sm border border-emerald-300 rounded-lg bg-white focus:outline-none"
                              style={{ boxShadow: `0 0 0 2px ${COLOR}30` }}
                            />
                            {savingEdit ? (
                              <Loader2 size={14} className="animate-spin text-emerald-400" />
                            ) : (
                              <>
                                <button
                                  onClick={() => handleSaveEdit(skill)}
                                  className="p-1 rounded-md bg-emerald-50 hover:bg-emerald-100 transition-all"
                                  title="Save"
                                >
                                  <Check size={14} color={COLOR} />
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="p-1 rounded-md bg-gray-100 hover:bg-gray-200 transition-all"
                                  title="Cancel"
                                >
                                  <X size={14} color="#9CA3AF" />
                                </button>
                              </>
                            )}
                          </div>
                        ) : (
                          <span className={`font-medium ${skill.isActive ? "text-gray-800" : "text-gray-400"}`}>
                            {skill.name}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Status Toggle */}
                    <td className="p-2 text-center">
                      {togglingId === skill.id ? (
                        <Loader2 size={15} className="animate-spin text-gray-400 mx-auto" />
                      ) : (
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={skill.isActive}
                            onChange={() => handleToggle(skill)}
                            className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                          />
                          <span className="ml-2 text-xs font-semibold">Active</span>
                        </label>
                      )}
                    </td>

                    {/* Edit Button */}
                    <td className="p-2 text-center">
                      {editingId !== skill.id && (
                        <button
                          onClick={() => startEdit(skill)}
                          className="p-1.5 rounded-lg hover:bg-emerald-50 transition-all mx-auto block"
                          title="Edit skill name"
                        >
                          <Edit size={14} color={COLOR} />
                        </button>
                      )}
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="py-16 text-center">
          <Loader2 size={28} className="animate-spin mx-auto mb-3" style={{ color: COLOR }} />
          <p className="text-sm text-gray-400">Loading skills...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && skills.length === 0 && (
        <div className="py-16 text-center">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ background: "#ECFDF5 "}}>
            <Zap size={24} color={COLOR} />
          </div>
          <p className="text-xs text-gray-400 mt-1">Click <strong>Add Skill</strong> to get started.</p>
        </div>
      )}

    </div>
  );
}